import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BRAND } from "../data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/featured-trails", label: "Featured Trails" },
  { to: "/interpretive-signage", label: "Interpretive Signage" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/become-a-storyteller", label: "Become a Storyteller" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email: email.trim() });
      toast.success("Thank you — we will be in touch.");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-brand-ink text-brand-cream/90 mt-20">
      <div className="container-px py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <img
            src={BRAND.logo}
            alt="Talking Trails"
            className="h-20 md:h-24 w-auto object-contain mb-6"
          />
          <div className="overline text-brand-ochre">Talking Trails</div>
          <h3 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-cream">
            Every Place Has a Story.
          </h3>
          <p className="mt-6 max-w-md text-brand-cream/70 font-light leading-relaxed">
            We help communities, Traditional Owners, parks, museums and tourism operators
            transform places into living visitor experiences through storytelling, audio
            interpretation and connection to Country.
          </p>

          <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-10 max-w-md">
            <label className="overline text-brand-cream/60">Newsletter</label>
            <div className="mt-3 flex items-center border-b border-brand-cream/30 focus-within:border-brand-ochre transition-colors">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent py-3 outline-none text-brand-cream placeholder-brand-cream/40"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="text-brand-ochre hover:text-brand-cream transition-colors p-2 disabled:opacity-50"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <p className="mt-3 text-xs text-brand-cream/40">
              Field notes, new trails and stories. No spam.
            </p>
          </form>
        </div>

        <div className="lg:col-span-4 lg:col-start-7">
          <div className="overline text-brand-cream/60">Navigate</div>
          <ul className="mt-5 grid grid-cols-2 gap-y-3 gap-x-6">
            {LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}
                  className="text-sm text-brand-cream/80 hover:text-brand-ochre transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 lg:col-start-11">
          <div className="overline text-brand-cream/60">Connect</div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a
                href="mailto:william@talkingtrails.com"
                className="flex items-center gap-3 text-brand-cream/80 hover:text-brand-ochre transition-colors"
                data-testid="footer-email"
              >
                <Mail size={15} /> william@talkingtrails.com
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-brand-cream/80 hover:text-brand-ochre transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin size={15} /> LinkedIn
              </a>
            </li>
            <li>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-brand-cream/80 hover:text-brand-ochre transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={15} /> Instagram
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-brand-cream/10">
        <div className="container-px py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-brand-cream/50">
          <div>© {new Date().getFullYear()} Talking Trails · www.talkingtrails.com</div>
          <div className="tracking-widest uppercase text-brand-cream/40">
            Listening · Place · Community · Country
          </div>
        </div>
      </div>
    </footer>
  );
}
